import { useState, useEffect, useMemo, useCallback } from 'react';
import { BookOpen, Search, X, BookMarked, ChevronLeft, ChevronRight, Loader2, GraduationCap, ExternalLink, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/common/PageHeader';
import { DataCard } from '../components/common/DataCard';
import { StatsCard } from '../components/common/StatsCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { useApp } from '../contexts/AppContext';
import { NcertPdfReader } from '../components/library/NcertPdfReader';
import { GutenbergReader } from '../components/library/GutenbergReader';
import { CreateFromChapterMenu } from '../components/library/CreateFromChapterMenu';
import { contentEngineApi } from '../services/contentEngineApi';
import NCERT_BOOKS_DATA, { NCERT_CLASSES, NCERT_SUBJECTS } from '../data/ncertBooks';

// ─── CATEGORIES → GUTENDEX TOPIC MAPPING ───
const CATEGORY_MAP = {
  'Adventure': 'adventure',
  'Art & Photography': 'art',
  'Autobiography & Memoir': 'autobiography',
  'Biography': 'biography',
  'Business & Economics': 'economics',
  'Children\'s Literature': 'children',
  'Classics': 'classics',
  'Crime & Mystery': 'detective',
  'Drama': 'drama',
  'Education': 'education',
  'Fantasy': 'fantasy',
  'Fiction': 'fiction',
  'Folklore & Mythology': 'folklore',
  'Geography & Travel': 'travel',
  'Health & Medicine': 'medicine',
  'History': 'history',
  'Horror & Supernatural': 'horror',
  'Humor & Satire': 'humor',
  'Language & Linguistics': 'language',
  'Law': 'law',
  'Literature': 'literature',
  'Mathematics': 'mathematics',
  'Music': 'music',
  'Nature & Environment': 'nature',
  'Philosophy': 'philosophy',
  'Poetry': 'poetry',
  'Politics & Government': 'politics',
  'Psychology': 'psychology',
  'Religion & Spirituality': 'religion',
  'Romance': 'romance',
  'Science': 'science',
  'Science Fiction': 'science fiction',
  'Short Stories': 'short stories',
  'Sociology': 'sociology',
  'Technology & Engineering': 'technology',
  'Theatre': 'theatre',
  'War & Military': 'war',
};

const CATEGORIES = Object.keys(CATEGORY_MAP);

function getReadableUrl(formats, gutenbergId) {
  // Use direct cache URL to avoid HTTP redirect (mixed content) issues in iframes
  if (gutenbergId) {
    return `https://www.gutenberg.org/cache/epub/${gutenbergId}/pg${gutenbergId}-images.html`;
  }
  return formats['text/html; charset=utf-8']
    || formats['text/html']
    || formats['text/html; charset=us-ascii']
    || formats['text/html; charset=iso-8859-1']
    || null;
}

function parseGutendexBook(b) {
  const htmlUrl = getReadableUrl(b.formats || {}, b.id);
  return {
    id: `gut-${b.id}`,
    gutenbergId: b.id,
    title: b.title,
    author: (b.authors || []).map(a => a.name).join(', ') || 'Unknown',
    link: `https://www.gutenberg.org/ebooks/${b.id}`,
    readLink: htmlUrl,
    downloads: b.download_count || 0,
  };
}

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function LibraryPage() {
  const { currentUser } = useApp();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
  const canRead = !isAdmin; // Teachers, students, parents can read
  const isTeacher = currentUser?.role === 'teacher';
  const isIndividual = isTeacher && !currentUser?.schoolId;
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [source, setSource] = useState('international');
  const [readBook, setReadBook] = useState(null);
  const [isDark, setIsDark] = useState(false);

  // NCERT filters
  const [ncertClass, setNcertClass] = useState('all');
  const [ncertSubject, setNcertSubject] = useState('all');
  const [ncertContext, setNcertContext] = useState({ filter: 'all', classes: [] });
  const [ncertTab, setNcertTab] = useState('textbooks'); // 'textbooks' (embedded) | 'archive' (external)

  // Gutenberg dynamic state
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(32);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  // Detect dark mode
  useEffect(() => {
    const check = () => setIsDark(document.documentElement.classList.contains('dark'));
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Fetch NCERT context for RBAC
  useEffect(() => {
    const fetchContext = async () => {
      try {
        const token = localStorage.getItem('sip_token');
        const res = await fetch(`${API_URL}/api/library/ncert-context`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setNcertContext(data);
          // Auto-set class filter for students/parents with restricted classes
          if (data.filter === 'class' && data.classes.length === 1) {
            setNcertClass(String(data.classes[0]));
          }
        }
      } catch (e) {
        console.error('Failed to fetch NCERT context:', e);
      }
    };
    fetchContext();
  }, []);

  // Fetch Gutenberg books via backend proxy
  const fetchGutenberg = useCallback(async (cat, searchQ, pg, pgSize) => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/library/gutenberg?page=${pg}&page_size=${pgSize}`;
      if (cat && cat !== 'all') {
        url += `&topic=${encodeURIComponent(CATEGORY_MAP[cat] || cat)}`;
      }
      if (searchQ) {
        url += `&search=${encodeURIComponent(searchQ)}`;
      }
      const token = localStorage.getItem('sip_token');
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 120000);
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (!res.ok) throw new Error('API error');
      const data = await res.json();
      setBooks((data.results || []).map(parseGutendexBook));
      setTotalCount(data.count || 0);
      setHasNext(!!data.next);
      setHasPrev(!!data.previous);
    } catch (e) {
      if (e.name !== 'AbortError') console.error('Gutenberg fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Single effect for Gutenberg fetches
  useEffect(() => {
    if (source !== 'international') return;
    const timer = setTimeout(() => {
      fetchGutenberg(category, search, page, pageSize);
    }, search ? 500 : 0);
    return () => clearTimeout(timer);
  }, [source, category, search, page, pageSize, fetchGutenberg]);

  // NCERT filtered with RBAC + class/subject filters + tab split
  const ncertFiltered = useMemo(() => {
    if (source !== 'ncert') return [];
    const q = search.toLowerCase();

    // Determine which classes are allowed (RBAC)
    let allowedClasses = null; // null means all
    if (ncertContext.filter === 'class' && ncertContext.classes.length > 0) {
      allowedClasses = ncertContext.classes;
    }

    return NCERT_BOOKS_DATA.filter(b => {
      // Tab filter: textbooks = has pdfCode, archive = no pdfCode
      if (ncertTab === 'textbooks' && !b.pdfCode) return false;
      if (ncertTab === 'archive' && b.pdfCode) return false;
      // RBAC class restriction
      if (allowedClasses && !allowedClasses.includes(b.classNum)) return false;
      // Class filter
      if (ncertClass !== 'all' && b.classNum !== Number(ncertClass)) return false;
      // Subject filter
      if (ncertSubject !== 'all' && b.subject !== ncertSubject) return false;
      // Search
      if (q) {
        const matchTitle = b.title.toLowerCase().includes(q);
        const matchSubject = b.subject.toLowerCase().includes(q);
        const matchClass = `class ${b.classNum}`.includes(q) || String(b.classNum).includes(q);
        if (!matchTitle && !matchSubject && !matchClass) return false;
      }
      return true;
    });
  }, [search, source, ncertClass, ncertSubject, ncertContext, ncertTab]);

  // Available NCERT classes (respects RBAC)
  const availableNcertClasses = useMemo(() => {
    if (ncertContext.filter === 'class' && ncertContext.classes.length > 0) {
      return ncertContext.classes;
    }
    return NCERT_CLASSES;
  }, [ncertContext]);

  const displayBooks = source === 'international' ? books : ncertFiltered;
  // NCERT is local data — never block it behind an in-flight Gutenberg fetch.
  const isLoading = source === 'international' && loading;
  const displayCount = source === 'international' ? totalCount : ncertFiltered.length;

  const handleCategoryChange = (val) => {
    setCategory(val);
    setPage(1);
  };

  const handleSourceChange = (val) => {
    setSource(val);
    setPage(1);
    setSearch('');
    setCategory('all');
    setNcertClass('all');
    setNcertSubject('all');
    setNcertTab('textbooks');
    // Auto-set class for restricted users
    if (val === 'ncert' && ncertContext.filter === 'class' && ncertContext.classes.length === 1) {
      setNcertClass(String(ncertContext.classes[0]));
    }
  };

  const handleNcertClassChange = (val) => {
    setNcertClass(val);
  };

  const handleNcertSubjectChange = (val) => {
    setNcertSubject(val);
  };

  const handlePageSizeChange = (val) => {
    setPageSize(Number(val));
    setPage(1);
  };

  // NCERT chapter state
  const [ncertChapter, setNcertChapter] = useState(1);
  // Gutenberg chapter state (native text reader)
  const [gutChapters, setGutChapters] = useState([]);
  const [gutChapter, setGutChapter] = useState(0);
  const [gutError, setGutError] = useState('');

  const closeReader = () => {
    setReadBook(null);
    setGutChapters([]);
    setGutChapter(0);
    setGutError('');
  };

  const loadGutenbergChapters = (book) => {
    setGutChapters([]);
    setGutChapter(0);
    setGutError('');
    contentEngineApi.readerGutenbergBook(book.gutenbergId)
      .then((r) => { setGutChapters(r.chapters || []); setGutChapter(r.chapters?.[0]?.number || 0); })
      .catch((e) => setGutError(e.message || 'Could not load this book'));
  };

  const openReader = (book) => {
    if (source === 'ncert') {
      if (book.pdfCode && book.maxChapter > 0 && ncertTab === 'textbooks') {
        setNcertChapter(1);
        setReadBook(book);
      } else {
        window.open(book.link, '_blank');
      }
    } else {
      setReadBook(book);
      if (book.gutenbergId) loadGutenbergChapters(book);
    }
  };

  return (
    <div data-testid="library-page" className="space-y-6 animate-fade-in">
      {isIndividual && (
        <Button size="sm" variant="ghost" className="h-8 -mb-2 text-xs" onClick={() => navigate('/teacher-assignments')} data-testid="library-back-to-workspace">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to workspace
        </Button>
      )}
      <PageHeader title="Library" description="Browse and read books from international and national collections." />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatsCard title="Total Books" value={isLoading ? '...' : displayCount.toLocaleString()} icon={BookOpen} delay={0} />
        <StatsCard
          title={source === 'ncert' ? 'Subjects' : 'Categories'}
          value={source === 'ncert' ? NCERT_SUBJECTS.length : CATEGORIES.length}
          icon={source === 'ncert' ? GraduationCap : BookMarked}
          delay={0.05}
        />
        <DataCard className="flex items-center gap-3 p-4" data-testid="showing-card">
          <div className="flex-1">
            <p className="text-xs text-muted-foreground mb-1">Per Page</p>
            {source === 'international' ? (
              <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="h-8 text-sm font-semibold" data-testid="page-size-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="32">32</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm font-semibold">{displayBooks.length}</p>
            )}
          </div>
        </DataCard>
        <DataCard className="flex items-center gap-3 p-4" data-testid="source-card">
          <div className="flex-1">
            <p className="text-xs text-muted-foreground mb-1">Source</p>
            <Select value={source} onValueChange={handleSourceChange}>
              <SelectTrigger className="h-8 text-sm font-semibold" data-testid="source-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="international">International</SelectItem>
                <SelectItem value="ncert">NCERT</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </DataCard>
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={source === 'ncert' ? 'Search by title, subject, or class...' : 'Search by title, author, or category...'}
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-10"
            data-testid="library-search"
          />
        </div>
        {source === 'international' && (
          <Select value={category} onValueChange={handleCategoryChange}>
            <SelectTrigger className="w-full sm:w-[220px]" data-testid="library-category-filter">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {source === 'ncert' && (
          <>
            <Select value={ncertClass} onValueChange={handleNcertClassChange}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="ncert-class-filter">
                <SelectValue placeholder="All Classes" />
              </SelectTrigger>
              <SelectContent>
                {availableNcertClasses.length > 1 && <SelectItem value="all">All Classes</SelectItem>}
                {availableNcertClasses.map(c => (
                  <SelectItem key={c} value={String(c)}>Class {c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={ncertSubject} onValueChange={handleNcertSubjectChange}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="ncert-subject-filter">
                <SelectValue placeholder="All Subjects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subjects</SelectItem>
                {NCERT_SUBJECTS.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
      </div>

      {/* NCERT Sub-tabs */}
      {source === 'ncert' && (
        <div className="flex items-center gap-1 p-1 bg-muted/50 rounded-lg w-fit" data-testid="ncert-subtabs">
          <button
            onClick={() => setNcertTab('textbooks')}
            className={`px-4 py-1.5 text-xs font-medium rounded-md transition-colors ${ncertTab === 'textbooks' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="ncert-tab-textbooks"
          >
            Textbooks ({NCERT_BOOKS_DATA.filter(b => b.pdfCode).length})
          </button>
          <button
            onClick={() => setNcertTab('archive')}
            className={`px-4 py-1.5 text-xs font-medium rounded-md transition-colors ${ncertTab === 'archive' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="ncert-tab-archive"
          >
            Archive ({NCERT_BOOKS_DATA.filter(b => !b.pdfCode).length})
          </button>
        </div>
      )}
      {isLoading && (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-brand-deep-blue" />
          <p className="text-sm text-muted-foreground">Fetching books from {source === 'international' ? 'International Library' : 'NCERT'}...</p>
          {source === 'international' && <p className="text-xs text-muted-foreground">First load may take a moment</p>}
        </div>
      )}

      {/* Book Grid */}
      {!isLoading && displayBooks.length === 0 && (
        <DataCard className="text-center py-16">
          <BookOpen className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-sm text-muted-foreground">No books found. Try a different search or category.</p>
        </DataCard>
      )}

      {!isLoading && displayBooks.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="library-grid">
          {displayBooks.map(book => (
            <DataCard key={book.id} className="flex flex-col justify-between hover:border-brand-light-blue/40 transition-colors" data-testid={`book-${book.id}`}>
              <div>
                <h4 className="text-sm font-semibold leading-tight line-clamp-2" title={book.title}>{book.title}</h4>
                {/* International book details */}
                {source === 'international' && (
                  <>
                    <p className="text-xs text-muted-foreground mt-1 truncate">{book.author}</p>
                    {book.category && <Badge className="mt-2 text-[10px] bg-brand-deep-blue/10 text-brand-deep-blue dark:bg-brand-deep-blue/20">{book.category}</Badge>}
                    {book.downloads > 0 && <p className="text-[10px] text-muted-foreground mt-1">{book.downloads.toLocaleString()} downloads</p>}
                  </>
                )}
                {/* NCERT book details */}
                {source === 'ncert' && (
                  <>
                    <p className="text-xs text-muted-foreground mt-1">NCERT</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      <Badge className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Class {book.classNum}</Badge>
                      <Badge className="text-[10px] bg-brand-deep-blue/10 text-brand-deep-blue dark:bg-brand-deep-blue/20">{book.subject}</Badge>
                    </div>
                  </>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                {source === 'international' && !isAdmin && book.readLink && (
                  <Button size="sm" className="w-full h-7 text-xs bg-brand-deep-blue hover:bg-brand-deep-blue/90" onClick={() => openReader(book)} data-testid={`read-${book.id}`}>
                    <BookOpen className="w-3 h-3 mr-1" /> Read
                  </Button>
                )}
                {source === 'ncert' && canRead && (
                  <Button size="sm" className="w-full h-7 text-xs bg-brand-deep-blue hover:bg-brand-deep-blue/90" onClick={() => openReader(book)} data-testid={`read-${book.id}`}>
                    {ncertTab === 'textbooks' ? (
                      <><BookOpen className="w-3 h-3 mr-1" /> Read</>
                    ) : (
                      <><ExternalLink className="w-3 h-3 mr-1" /> Open on NCERT</>
                    )}
                  </Button>
                )}
                {isAdmin && (
                  <p className="text-[10px] text-center text-muted-foreground py-1">View only</p>
                )}
              </div>
            </DataCard>
          ))}
        </div>
      )}

      {/* Pagination (Gutenberg only) */}
      {source === 'international' && !isLoading && displayBooks.length > 0 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-muted-foreground">{totalCount.toLocaleString()} books found · Page {page} · {displayBooks.length} shown</p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={!hasPrev} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="prev-page">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="outline" disabled={!hasNext} onClick={() => setPage(p => p + 1)} data-testid="next-page">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Read Dialog */}
      <Dialog open={!!readBook} onOpenChange={() => closeReader()}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0">
          <DialogHeader className="p-4 pb-2 border-b dark:border-slate-700 shrink-0">
            <DialogTitle className="text-base pr-8">{readBook?.title}</DialogTitle>
            <DialogDescription className="text-xs">
              {readBook?.author || (readBook?.pdfCode ? `NCERT · Class ${readBook.classNum} · ${readBook.subject}` : 'NCERT')}
            </DialogDescription>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {/* NCERT chapter navigation */}
              {readBook?.pdfCode && readBook?.maxChapter > 0 && (
                <>
                  <Button size="sm" variant="outline" className="h-7 text-xs" disabled={ncertChapter <= 1} onClick={() => { setNcertChapter(c => Math.max(1, c - 1)); }} data-testid="ncert-prev-chapter">
                    <ChevronLeft className="w-3 h-3" />
                  </Button>
                  <Select value={String(ncertChapter)} onValueChange={v => { setNcertChapter(Number(v)); }}>
                    <SelectTrigger className="h-7 w-[140px] text-xs" data-testid="ncert-chapter-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: readBook.maxChapter }, (_, i) => i + 1).map(ch => (
                        <SelectItem key={ch} value={String(ch)}>Chapter {ch}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" className="h-7 text-xs" disabled={ncertChapter >= readBook.maxChapter} onClick={() => { setNcertChapter(c => Math.min(readBook.maxChapter, c + 1)); }} data-testid="ncert-next-chapter">
                    <ChevronRight className="w-3 h-3" />
                  </Button>
                </>
              )}
              {/* Gutenberg chapter navigation */}
              {readBook?.gutenbergId && gutChapters.length > 0 && (() => {
                const idx = gutChapters.findIndex((c) => c.number === gutChapter);
                return (
                  <>
                    <Button size="sm" variant="outline" className="h-7 text-xs" disabled={idx <= 0} onClick={() => setGutChapter(gutChapters[idx - 1].number)} data-testid="gutenberg-prev-chapter">
                      <ChevronLeft className="w-3 h-3" />
                    </Button>
                    <Select value={String(gutChapter)} onValueChange={(v) => setGutChapter(Number(v))}>
                      <SelectTrigger className="h-7 w-[200px] text-xs" data-testid="gutenberg-chapter-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {gutChapters.map((c) => (
                          <SelectItem key={c.number} value={String(c.number)}>{c.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" className="h-7 text-xs" disabled={idx >= gutChapters.length - 1} onClick={() => setGutChapter(gutChapters[idx + 1].number)} data-testid="gutenberg-next-chapter">
                      <ChevronRight className="w-3 h-3" />
                    </Button>
                  </>
                );
              })()}
              <div className="ml-auto flex items-center gap-2">
                {isTeacher && readBook?.pdfCode && (
                  <CreateFromChapterMenu
                    payload={{ source: 'ncert', code: readBook.pdfCode, chapter: ncertChapter, title: readBook.title, classNum: String(readBook.classNum || ''), subject: readBook.subject }}
                    chapterNumber={ncertChapter}
                    curriculum={{ board: 'cbse', classId: String(readBook.classNum || ''), subject: readBook.subject }}
                  />
                )}
                {isTeacher && readBook?.gutenbergId && (
                  <CreateFromChapterMenu
                    payload={{ source: 'gutenberg', gutenbergId: readBook.gutenbergId }}
                    chapterNumber={gutChapter}
                    curriculum={{ subject: 'English Literature' }}
                    disabled={!gutChapter}
                  />
                )}
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => closeReader()} data-testid="reader-close">
                  <X className="w-3 h-3 mr-1" /> Close
                </Button>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-white">
            {/* Gutenberg — native in-app text reader (no iframe / no redirect) */}
            {readBook?.gutenbergId && !readBook?.pdfCode && (
              gutError ? (
                <div className="h-full grid place-items-center text-sm text-muted-foreground p-6 text-center" data-testid="gutenberg-book-error">{gutError}</div>
              ) : gutChapter ? (
                <GutenbergReader gutenbergId={readBook.gutenbergId} chapter={gutChapter} />
              ) : (
                <div className="h-full grid place-items-center" data-testid="gutenberg-book-loading"><Loader2 className="w-5 h-5 animate-spin text-ae-blue" /></div>
              )
            )}
            {/* NCERT chapter — rendered in-app with pdf.js (no browser PDF viewer / redirect) */}
            {readBook?.pdfCode && (
              <NcertPdfReader
                code={readBook.pdfCode}
                chapter={ncertChapter}
                title={`${readBook.title} - Chapter ${ncertChapter}`}
              />
            )}
            {readBook && !readBook.gutenbergId && !readBook.pdfCode && (
              <div className="flex flex-col items-center justify-center h-full gap-3">
                <BookOpen className="w-10 h-10 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">No readable version available for this book.</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
